import React, { useState } from 'react';
import Header from '../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Download, Package, Database, Code, FileText, 
  CheckCircle, Server, Layers, FolderArchive
} from 'lucide-react';

const DownloadPage = () => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = (format = 'zip') => {
    setDownloading(true);
    // Trigger download
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    const endpoint = format === 'tar' ? '/download-project-tar' : '/download-project';
    window.location.href = `${backendUrl}${endpoint}`;
    
    // Reset downloading state after 2 seconds
    setTimeout(() => setDownloading(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Header />
      
      <div className="pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mb-6">
              <Package className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Download Complete Project
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get the entire Gujarat WCD Analytics Platform including database, backend, and frontend
            </p>
          </div>

          {/* Main Download Card */}
          <Card className="mb-8 border-2 border-blue-200 shadow-xl">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50">
              <CardTitle className="flex items-center text-2xl">
                <FolderArchive className="w-8 h-8 mr-3 text-blue-600" />
                WCD Analytics Complete Package
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="mb-6">
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Choose Download Format:</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="p-4 border-2 border-blue-300 rounded-lg bg-blue-50">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-gray-900">ZIP Format</h4>
                          <p className="text-sm text-gray-600">wcd_complete_with_database.zip</p>
                          <p className="text-xs text-gray-500 mt-1">Size: ~121 MB</p>
                        </div>
                        <Badge className="bg-blue-600">Recommended</Badge>
                      </div>
                      <Button 
                        onClick={() => handleDownload('zip')}
                        disabled={downloading}
                        className="w-full mt-3 bg-blue-600 hover:bg-blue-700"
                      >
                        {downloading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Downloading...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-2" />
                            Download ZIP
                          </>
                        )}
                      </Button>
                    </div>

                    <div className="p-4 border-2 border-green-300 rounded-lg bg-green-50">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-gray-900">TAR.GZ Format</h4>
                          <p className="text-sm text-gray-600">wcd_complete_with_database.tar.gz</p>
                          <p className="text-xs text-gray-500 mt-1">Size: ~90 MB</p>
                        </div>
                        <Badge className="bg-green-600">Smaller</Badge>
                      </div>
                      <Button 
                        onClick={() => handleDownload('tar')}
                        disabled={downloading}
                        className="w-full mt-3 bg-green-600 hover:bg-green-700"
                      >
                        {downloading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Downloading...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-2" />
                            Download TAR.GZ
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  What's Included:
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
                  <div className="flex items-start gap-2">
                    <Database className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong>Complete Database</strong>
                      <p className="text-xs text-gray-600">MongoDB dump (34+ collections)</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Server className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong>Backend Code</strong>
                      <p className="text-xs text-gray-600">FastAPI + ETL scripts</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Code className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong>Frontend Code</strong>
                      <p className="text-xs text-gray-600">React app + 60+ components</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong>Excel Files</strong>
                      <p className="text-xs text-gray-600">Sample data files</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Package Contents */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Layers className="w-6 h-6 mr-3 text-purple-600" />
                Package Contents
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-green-50 rounded-lg border-l-4 border-green-500">
                  <div className="flex items-start gap-3">
                    <Database className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-green-900 mb-2">Database (MongoDB Dump)</h4>
                      <ul className="space-y-1 text-sm text-gray-700">
                        <li>• 8 WCD program initiatives with complete data</li>
                        <li>• 9 dashboard collections (Field Monitoring, THR, HCM, etc.)</li>
                        <li>• 14,950 beneficiary records with 99.84% Aadhaar verification</li>
                        <li>• 971,176 PURNA program enrollments</li>
                        <li>• 33 Gujarat districts with real-time data</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                  <div className="flex items-start gap-3">
                    <Server className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-blue-900 mb-2">Backend (FastAPI)</h4>
                      <ul className="space-y-1 text-sm text-gray-700">
                        <li>• Complete FastAPI application with all routes</li>
                        <li>• ETL scripts for data processing (10+ scripts)</li>
                        <li>• Pydantic models for data validation</li>
                        <li>• Dashboard API endpoints for all 9 dashboards</li>
                        <li>• Authentication and authorization modules</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-purple-50 rounded-lg border-l-4 border-purple-500">
                  <div className="flex items-start gap-3">
                    <Code className="w-6 h-6 text-purple-600 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-purple-900 mb-2">Frontend (React)</h4>
                      <ul className="space-y-1 text-sm text-gray-700">
                        <li>• React 18 application with React Router</li>
                        <li>• 20+ pages including home, dashboards, analysis tools</li>
                        <li>• Shadcn UI components + Tailwind CSS</li>
                        <li>• Recharts visualizations for all dashboards</li>
                        <li>• Responsive design for mobile/tablet/desktop</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-500">
                  <div className="flex items-start gap-3">
                    <FileText className="w-6 h-6 text-yellow-600 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-yellow-900 mb-2">Documentation & Setup</h4>
                      <ul className="space-y-1 text-sm text-gray-700">
                        <li>• Comprehensive README with setup instructions</li>
                        <li>• Automated setup.sh script for easy installation</li>
                        <li>• Database restoration commands</li>
                        <li>• Environment configuration examples</li>
                        <li>• Deployment guidelines and best practices</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Setup Instructions Preview */}
          <Card className="mb-8 bg-gradient-to-r from-gray-800 to-gray-900 text-white">
            <CardHeader>
              <CardTitle className="text-white">Quick Setup (After Download)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 font-mono text-sm">
                <div>
                  <div className="text-gray-400 mb-1"># 1. Extract the zip file</div>
                  <div className="bg-black/30 p-3 rounded">unzip wcd_analytics_complete.zip</div>
                </div>
                <div>
                  <div className="text-gray-400 mb-1"># 2. Run automated setup</div>
                  <div className="bg-black/30 p-3 rounded">cd wcd_project_backup && ./setup.sh</div>
                </div>
                <div>
                  <div className="text-gray-400 mb-1"># 3. Start backend (Terminal 1)</div>
                  <div className="bg-black/30 p-3 rounded">cd backend && uvicorn server:app --reload</div>
                </div>
                <div>
                  <div className="text-gray-400 mb-1"># 4. Start frontend (Terminal 2)</div>
                  <div className="bg-black/30 p-3 rounded">cd frontend && yarn start</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Features List */}
          <Card>
            <CardHeader>
              <CardTitle>Complete Feature Set</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Dashboards (9)</h4>
                  <ul className="space-y-1 text-sm text-gray-700">
                    <li>✅ Field Monitoring & AVT</li>
                    <li>✅ THR Distribution & Leakages</li>
                    <li>✅ Aadhaar & FRS Authentication</li>
                    <li>✅ Hot Cooked Meal (HCM)</li>
                    <li>✅ Growth Monitoring Quality</li>
                    <li>✅ Enrollment & Drop-off</li>
                    <li>✅ AWC Infrastructure</li>
                    <li>✅ PURNA & Poshan Sangam</li>
                    <li>✅ Interactive Gujarat Map</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Analysis Tools</h4>
                  <ul className="space-y-1 text-sm text-gray-700">
                    <li>✅ Beneficiary Enrollment Analysis</li>
                    <li>✅ Cross-enrollment tracking</li>
                    <li>✅ Drop-off trend analysis</li>
                    <li>✅ Root cause identification</li>
                    <li>✅ System gap detection</li>
                    <li>✅ Executive KPIs dashboard</li>
                    <li>✅ Implementation roadmap</li>
                    <li>✅ Real-time data integration</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DownloadPage;
